import { createValidationIssue } from "../issues.js";
import { addressImportNotice } from "../../utils/addressRoutingValidation.js";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeSummaryText(summary) {
  return typeof summary === "string" ? summary.trim() : "";
}

function normalizeRetryAfterSeconds(value) {
  const parsedValue = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function formatRetryAfterSeconds(value) {
  const totalSeconds = normalizeRetryAfterSeconds(value);
  if (totalSeconds === null) {
    return "";
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    return `${minutes} min ${seconds} s`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return `${seconds} s`;
}

function isImportRateLimitedResponse({ payload, response } = {}) {
  return response?.status === 429 || payload?.code === "rate_limited";
}

function buildImportRateLimitedMessage({ payload, response } = {}) {
  const retryAfterLabel = formatRetryAfterSeconds(
    payload?.retry_after_seconds ?? response?.headers?.get?.("Retry-After"),
  );
  const resourceLabel = payload?.resource === "import"
    ? "cet import"
    : "cette opération";

  return retryAfterLabel
    ? `Le serveur a temporairement bloqué ${resourceLabel} après trop d'essais rapprochés. Cette garde anti-répétition reste active même en local. Réessayez dans ${retryAfterLabel}.`
    : `Le serveur a temporairement bloqué ${resourceLabel} après trop d'essais rapprochés. Cette garde anti-répétition reste active même en local. Réessayez un peu plus tard.`;
}

function resolveImportFeedbackSeverity(impact) {
  return isPlainObject(impact) && impact.level === "minor" ? "warning" : "info";
}

function hasSnapshotStaleFollowUp(impact) {
  if (!isPlainObject(impact) || impact.has_real_changes === false) {
    return false;
  }

  const signals = isPlainObject(impact.signals) ? impact.signals : {};
  return Boolean(signals.snapshot_active);
}

function hasOnlyUnchangedImportResults(results) {
  return Array.isArray(results)
    && results.length > 0
    && results.every((result) => result?.status === "unchanged");
}

function resolvePreservedPullForwardDates(payload) {
  const pgSync = isPlainObject(payload?.pg_sync) ? payload.pg_sync : {};
  const count = Math.max(
    0,
    Number.parseInt(String(pgSync.preserved_pull_forward_dates ?? 0), 10) || 0,
  );
  if (count === 0) {
    return null;
  }
  return {
    count,
    details: Array.isArray(pgSync.preserved_pull_forward_date_details)
      ? pgSync.preserved_pull_forward_date_details
      : [],
    message: normalizeSummaryText(pgSync.notice) || "Dates devancées conservées",
  };
}

function appendPreservedPullForwardDatesFeedback(feedback, preservation) {
  if (!feedback || !preservation) {
    return feedback;
  }
  const preservationMessage = `${preservation.message} (${preservation.count}).`;
  const message = [normalizeSummaryText(feedback.message), preservationMessage]
    .filter(Boolean)
    .join(" ");
  return {
    ...feedback,
    issue: feedback.issue
      ? {
        ...feedback.issue,
        message,
        metadata: {
          ...(isPlainObject(feedback.issue.metadata) ? feedback.issue.metadata : {}),
          preservedPullForwardDateDetails: preservation.details,
          preservedPullForwardDates: preservation.count,
        },
      }
      : feedback.issue,
    message,
  };
}

function isImportGuardBlockedResponse({ fallbackErrorMessage, response, responseImpact } = {}) {
  return response?.status === 409
    && (
      responseImpact?.guard_blocked === true
      || (typeof fallbackErrorMessage === "string" && fallbackErrorMessage.startsWith("L'import Orders est bloqué"))
    );
}

export function buildImportFeedbackIssue({
  blocking = false,
  code = "import.feedback",
  field = "",
  message,
  metadata = null,
  scope = "import",
  severity = "info",
  source = "import",
} = {}) {
  return createValidationIssue({
    blocking,
    code,
    field,
    message,
    metadata,
    scope,
    severity,
    source,
  });
}

export function resolveImportErrorMessage({ payload, rawResponseText, response }) {
  if (isImportRateLimitedResponse({ payload, response })) {
    return buildImportRateLimitedMessage({ payload, response });
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  if (Array.isArray(payload?.results) && payload.results.length > 0) {
    return null;
  }

  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (typeof payload?.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }

  if (typeof rawResponseText === "string") {
    const trimmedResponseText = rawResponseText.trim();
    if (trimmedResponseText && !trimmedResponseText.startsWith("<!DOCTYPE") && !trimmedResponseText.startsWith("<html")) {
      return trimmedResponseText;
    }
  }

  if (response.status === 500) {
    return "Le serveur a renvoyé une erreur interne pendant l'import. Vérifiez la configuration backend Supabase et les logs du serveur.";
  }

  return `Le serveur a refusé l'import (${response.status} ${response.statusText || "erreur"}).`;
}

export function buildFallbackImportResult({ message, response }) {
  return {
    field: "request",
    filename: "",
    status: "error",
    message: message || `Erreur ${response.status}`,
  };
}

export function resolveImportResponseOutcome({ payload, rawResponseText, response }) {
  const responseResults = Array.isArray(payload?.results)
    ? payload.results
    : [];
  const rawImpact = isPlainObject(payload?.impact) ? payload.impact : null;
  const hasSyncFailure = ["error", "conflict", "rejected"].includes(payload?.pg_sync?.status);
  const canRequestConfirmation = !hasSyncFailure && (response.ok || response.status === 409);
  const requiresConfirmation = canRequestConfirmation
    && (payload?.requires_confirmation === true
      || (payload?.requires_confirmation == null && rawImpact?.requires_confirmation === true));
  const responseImpact = rawImpact && !requiresConfirmation && rawImpact.requires_confirmation
    ? { ...rawImpact, requires_confirmation: false }
    : rawImpact;
  const fallbackErrorMessage = resolveImportErrorMessage({
    payload,
    rawResponseText,
    response,
  });
  const results = responseResults.length > 0 || response.ok
    ? responseResults
    : [buildFallbackImportResult({ message: fallbackErrorMessage, response })];

  return {
    fallbackErrorMessage,
    requiresConfirmation,
    responseImpact,
    results,
  };
}

export function resolveImportBackendFeedbackState({ payload, rawResponseText, response, submissionMode = "selection" }) {
  const outcome = resolveImportResponseOutcome({
    payload,
    rawResponseText,
    response,
  });
  const shouldUseConfirmationFeedback = outcome.requiresConfirmation;

  let feedback = shouldUseConfirmationFeedback
    ? buildImportBackendErrorFeedback({
      fallbackErrorMessage: outcome.fallbackErrorMessage,
      payload,
      requiresConfirmation: outcome.requiresConfirmation,
      response,
      responseImpact: outcome.responseImpact,
    })
    : response.ok
      ? buildImportSuccessFeedback(outcome.responseImpact, outcome.results, submissionMode)
      : buildImportBackendErrorFeedback({
        fallbackErrorMessage: outcome.fallbackErrorMessage,
        payload,
        requiresConfirmation: outcome.requiresConfirmation,
        response,
        responseImpact: outcome.responseImpact,
      });

  const addressNotice = response.ok ? addressImportNotice(payload?.pg_sync?.address_validation_summary) : "";
  const coopNotice = response.ok ? coopImportNotice(payload?.pg_sync?.coop_clients) : "";
  if (addressNotice || coopNotice) {
    const message = [feedback?.message, addressNotice, coopNotice].filter(Boolean).join(" ");
    feedback = { ...feedback, message, ...(feedback?.issue ? { issue: { ...feedback.issue, message } } : {}) };
  }
  return {
    ...outcome,
    feedback: response.ok
      ? appendPreservedPullForwardDatesFeedback(
        feedback,
        resolvePreservedPullForwardDates(payload),
      )
      : feedback,
  };
}

function formatCoopClients(entries) {
  return entries.map((entry) => `${entry.client_name} (${entry.client_code})`).join(", ");
}

// Coops of the imported orders that became, or were marked as, Coop records in Clients.
export function coopImportNotice(coopClients) {
  const created = Array.isArray(coopClients?.created) ? coopClients.created : [];
  const flagged = Array.isArray(coopClients?.flagged) ? coopClients.flagged : [];
  const parts = [];
  if (created.length) {
    const plural = created.length > 1 ? "s" : "";
    parts.push(`${created.length} nouvelle${plural} coop${plural} ajoutée${plural} à Clients : ${formatCoopClients(created)}.`);
  }
  if (flagged.length) {
    const plural = flagged.length > 1 ? "s" : "";
    parts.push(`${flagged.length} fiche${plural} existante${plural} marquée${plural} Coop : ${formatCoopClients(flagged)}.`);
  }
  return parts.join(" ");
}

export function buildImportBackendErrorFeedback({
  fallbackErrorMessage,
  payload,
  requiresConfirmation = false,
  response,
  responseImpact,
} = {}) {
  if (requiresConfirmation) {
    const issue = buildImportFeedbackIssue({
      code: "import.confirmation_required",
      message: responseImpact?.summary || "Une confirmation explicite est requise avant le remplacement des sources actives.",
      metadata: {
        requiresConfirmation: true,
        status: response?.status || null,
      },
      severity: "warning",
      source: "backend",
    });

    return {
      issue,
      message: issue.message,
      tone: "warning",
    };
  }

  if (isImportGuardBlockedResponse({ fallbackErrorMessage, response, responseImpact })) {
    const issue = buildImportFeedbackIssue({
      blocking: true,
      code: "import.guard_blocked",
      field: "orders",
      message: fallbackErrorMessage || "L'import Orders est bloqué par une règle métier.",
      metadata: {
        status: response?.status || null,
        statusText: response?.statusText || "",
      },
      severity: "warning",
      source: "backend",
    });

    return {
      issue,
      message: issue.message,
      tone: "warning",
    };
  }

  if (isImportRateLimitedResponse({ payload, response })) {
    const issue = buildImportFeedbackIssue({
      blocking: true,
      code: "import.rate_limited",
      field: "request",
      message: fallbackErrorMessage || buildImportRateLimitedMessage({ payload, response }),
      metadata: {
        resource: typeof payload?.resource === "string" ? payload.resource : "import",
        retryAfterSeconds: normalizeRetryAfterSeconds(
          payload?.retry_after_seconds ?? response?.headers?.get?.("Retry-After"),
        ),
        status: response?.status || null,
        statusText: response?.statusText || "",
      },
      severity: "warning",
      source: "backend",
    });

    return {
      issue,
      message: issue.message,
      tone: "warning",
    };
  }

  const message = response?.status === 401
    ? "Le serveur a refusé l’import: le jeton Supabase est absent ou invalide."
    : fallbackErrorMessage || "L’import a été refusé ou partiellement échoué. Vérifiez le détail zone par zone ci-dessous.";
  const issue = buildImportFeedbackIssue({
    blocking: true,
    code: response?.status === 401 ? "import.unauthorized" : "import.backend_rejected",
    field: "request",
    message,
    metadata: {
      status: response?.status || null,
      statusText: response?.statusText || "",
    },
    severity: "error",
    source: "backend",
  });

  return {
    issue,
    message: issue.message,
    tone: "danger",
  };
}

export function buildImportSuccessFeedback(impact, results = [], submissionMode = "selection") {
  const defaultMessage = "Import terminé. Les fichiers source sélectionnés ont été remplacés sur le serveur.";
  const severity = resolveImportFeedbackSeverity(impact);
  const hasSnapshotFollowUp = hasSnapshotStaleFollowUp(impact);
  const summary = normalizeSummaryText(impact?.summary);

  if (hasOnlyUnchangedImportResults(results)) {
    const issue = buildImportFeedbackIssue({
      code: "import.no_changes",
      message: summary || "Aucun changement métier détecté entre l'import et les fichiers source actuels.",
      metadata: {
        hasSnapshotFollowUp: false,
        impactLevel: normalizeSummaryText(impact?.level) || "none",
      },
      severity: "info",
    });

    return {
      issue,
      message: issue.message,
      tone: "neutral",
    };
  }

  let code = "import.completed";
  let message = defaultMessage;

  if (!isPlainObject(impact)) {
    const issue = buildImportFeedbackIssue({
      code,
      message,
      severity,
    });

    return {
      issue,
      message: issue.message,
      tone: "success",
    };
  }

  if (hasSnapshotFollowUp) {
    // Remplacement complet : l'avis de fin ajoute déjà « … puis « Optimiser la flotte ». ».
    const isForceReplace = submissionMode === "force";
    code = isForceReplace ? "import.reoptimization_required" : "import.completed_with_dispatch_update";
    message = ["Import terminé.", summary, isForceReplace ? "" : "Ouvrez le Dispatch, puis « Mise à jour »."]
      .filter(Boolean)
      .join(" ");
  } else if (summary) {
    code = impact.level === "minor" ? "import.completed_with_minor_impact" : "import.completed_with_summary";
    message = `Import terminé. ${summary}`;
  }

  const issue = buildImportFeedbackIssue({
    code,
    message,
    metadata: {
      hasSnapshotFollowUp,
      impactLevel: normalizeSummaryText(impact?.level),
    },
    severity,
  });

  return {
    issue,
    message: issue.message,
    tone: issue.severity === "warning" ? "warning" : "success",
  };
}
